
import React from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { AlertCircle, HelpCircle } from "lucide-react";
import { SlackSettingsFormProps } from "./types";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

const SlackSettingsForm: React.FC<SlackSettingsFormProps> = ({
  triggerTag,
  onTagChange,
  isFileProcessingEnabled,
  onFileProcessingChange,
  isAutoResponsesEnabled,
  onAutoResponsesChange,
  isRoleDetectionEnabled = true,
  onRoleDetectionChange,
  onDisconnect,
  aiStaffName = "VirtuStaff",
  hideDisconnect = false
}) => {
  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h4 className="text-sm font-medium">Connection Settings</h4>
        <div className="grid gap-4">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Label htmlFor="trigger-tag">Trigger Tag</Label>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-xs">The bot will respond when this tag is used in a Slack message. For example: "@VirtuStaff"</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <Input 
              id="trigger-tag" 
              value={triggerTag} 
              onChange={(e) => onTagChange(e.target.value)} 
              placeholder="@VirtuStaff"
            />
            <p className="text-xs text-muted-foreground">
              When this tag is used in a message, {aiStaffName} will respond automatically.
            </p>
          </div>
        </div>
      </div>

      <Separator />

      <div className="space-y-4">
        <h4 className="text-sm font-medium">Integration Status</h4>
        
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label htmlFor="auto-responses" className="text-sm">Enable Integration</Label>
            <p className="text-xs text-muted-foreground">
              Toggle to enable or disable the Slack integration without deleting it
            </p>
          </div>
          <Switch 
            id="auto-responses" 
            checked={isAutoResponsesEnabled} 
            onCheckedChange={onAutoResponsesChange}
          />
        </div>

        <p className="text-xs text-muted-foreground mt-4 bg-blue-50 dark:bg-blue-900/20 p-3 rounded-md border border-blue-100 dark:border-blue-800">
          When enabled, {aiStaffName} will process all messages tagged with <strong>{triggerTag}</strong> or when mentioned directly with @VirtuStaff. 
          The AI will automatically detect the appropriate role for each request (Marketing, Sales, Customer Support) and respond accordingly.
        </p>
      </div>

      <Separator />

      {!hideDisconnect && (
        <div className="flex flex-col xs:flex-row justify-between gap-4">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={onDisconnect}
            className="text-red-500 border-red-200 hover:bg-red-50 hover:text-red-600"
          >
            Disconnect
          </Button>
          
          <div className="flex items-center text-xs text-muted-foreground gap-1">
            <AlertCircle className="h-3 w-3" />
            <span>Changes are saved automatically</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default SlackSettingsForm;
